import { formatCode, isValidCode, normalizeCode } from "@brouhaha/codes";
import { t } from "@brouhaha/i18n";
import type { MessageKey } from "@brouhaha/i18n";
import { BrouhahaMark, Button, Card } from "@brouhaha/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";

import { wantsThread } from "@brouhaha/protocol";
import type { PublicMember } from "@brouhaha/protocol";

import { setErrorTags } from "../lib/errors";
import { API_URL, LOBBY_URL } from "../lib/flags";
import { Breather, NightControls } from "../lobby/Breather";
import { GamePicker } from "../lobby/GamePicker";
import { Recap } from "../lobby/Recap";
import { TableVoteCard } from "../lobby/TableVoteCard";
import { TableSetupCard } from "../lobby/TableSetupCard";
import { AgeGate } from "../lobby/AgeGate";
import { hasPassedAgeGate } from "../lobby/age-store";
import { askTheDoor, localDevice } from "../lobby/door";
import type { DoorAnswer } from "../lobby/door";
import { OnlineGame } from "../lobby/OnlineGame";
import { refusalMessage, RETRIABLE } from "../lobby/refusals";
import { latestSeatElsewhere, loadSeat, markStacked } from "../lobby/seat-store";
import { AddCode, StackChoice, StackToast } from "../lobby/Stacking";
import { ThatsOne } from "../lobby/ThatsOne";
import { cachedSession, guestSession, localSessionStore, subjectOf } from "../lobby/session";
import { useLobby } from "../lobby/use-lobby";
import type { LobbyClient, LobbyClientState } from "../lobby/client";

/**
 * The scan route: /c/{code}. Name yourself, then the Table screen (design doc
 * 6.2), kept small because it is the first thing a phone downloads at the bar.
 * A phone that already has a seat here skips straight to the table. When the
 * host starts a game, the same screen becomes that game.
 */
export const Route = createFileRoute("/c/$code")({
  component: CodeRoute,
  // The QR address carries the code's signature as ?s=; a typed code has none.
  validateSearch: (search: Record<string, unknown>): { s?: string } =>
    typeof search["s"] === "string" && search["s"] !== "" ? { s: search["s"] } : {},
});

const SUGGESTIONS: MessageKey[] = [
  "nick.otter",
  "nick.pretzel",
  "nick.moose",
  "nick.pickle",
  "nick.walrus",
  "nick.biscuit",
  "nick.gazebo",
  "nick.koala",
];

function CodeRoute() {
  const { code: rawCode } = Route.useParams();
  // A new code is a new screen: nothing from the last one carries over.
  return <CodeScreen key={rawCode} rawCode={rawCode} />;
}

function CodeScreen({ rawCode }: { rawCode: string }) {
  const { s: signature } = Route.useSearch();
  const navigate = useNavigate();
  const code = normalizeCode(rawCode);
  const valid = isValidCode(code);
  // A phone that already sat at this very code skips the door: its token restores the seat.
  const [seat] = useState(() => (valid ? loadSeat(code) : null));
  // A fresh code scanned while sitting at another table tonight: stack it there,
  // or start a separate table (design doc 6.4). Asked before the code is spent.
  const [stackInto, setStackInto] = useState(() =>
    valid && !seat && API_URL ? (latestSeatElsewhere(code, Date.now())?.code ?? null) : null,
  );
  const door = useDoor(valid && !seat && !stackInto ? code : null, signature ?? null);
  const [joinAs, setJoinAs] = useState<{ nickname: string; token: string | null } | null>(() =>
    seat ? { nickname: seat.nickname, token: seat.token } : null,
  );

  // The table the door named: for a stacked code, not the code that was scanned.
  const admitted =
    door.answer.kind === "opened" || door.answer.kind === "joined" ? door.answer : null;
  const lobbyId = seat ? code : (admitted?.lobbyId ?? code);
  const ticket = seat ? (seat.ticket ?? null) : (admitted?.ticket ?? null);
  const session = door.session ?? cachedSession(localSessionStore());
  useEffect(() => {
    if (!admitted || admitted.lobbyId === code) return;
    const there = loadSeat(admitted.lobbyId);
    if (there) setJoinAs({ nickname: there.nickname, token: there.token });
  }, [admitted, code]);

  return (
    <main className="flex flex-1 flex-col gap-6">
      <header className="flex items-center gap-4">
        <BrouhahaMark size={56} />
        <div>
          <h1 className="text-xl font-bold">{valid ? formatCode(code) : t("code.title")}</h1>
          <p className="text-sm text-ink-muted">{t("app.tagline")}</p>
        </div>
      </header>

      {!valid ? (
        <Card>
          <p role="alert">{t("code.badCode")}</p>
        </Card>
      ) : !LOBBY_URL ? (
        <Card>
          <p role="alert">{t("join.notConfigured")}</p>
        </Card>
      ) : stackInto ? (
        <StackChoice
          code={code}
          signature={signature ?? null}
          into={stackInto}
          onStacked={(ticket) => {
            markStacked(stackInto, ticket);
            void navigate({ to: "/c/$code", params: { code: stackInto }, search: {} });
          }}
          onSeparate={() => setStackInto(null)}
        />
      ) : door.answer.kind === "asking" ? (
        <p role="status" className="text-sm text-ink-muted">
          {t("door.checking")}
        </p>
      ) : door.answer.kind === "rejected" || door.answer.kind === "unreachable" ? (
        <DoorRefused answer={door.answer} onRetry={door.retry} />
      ) : joinAs ? (
        <Table
          code={lobbyId}
          ticket={ticket}
          session={session}
          nickname={joinAs.nickname}
          token={joinAs.token}
          onLeave={() => setJoinAs(null)}
        />
      ) : (
        <NameYourself onJoin={(nickname) => setJoinAs({ nickname, token: null })} />
      )}

      {!joinAs && !stackInto && (
        <>
          <div className="flex-1" />
          <Link to="/" className="contents">
            <Button variant="ghost" full tabIndex={-1}>
              {t("code.back")}
            </Button>
          </Link>
        </>
      )}
    </main>
  );
}

type DoorState = DoorAnswer | { kind: "asking" } | { kind: "skipped" };

/**
 * Asks the API what the code means before anyone types a name. With no API
 * configured the door is skipped and the phone goes straight to the lobby.
 */
function useDoor(code: string | null, signature: string | null) {
  const [attempt, setAttempt] = useState(0);
  const [answer, setAnswer] = useState<DoorState>(() =>
    code && API_URL ? { kind: "asking" } : { kind: "skipped" },
  );
  const [session, setSession] = useState<string | null>(null);
  useEffect(() => {
    if (!code || !API_URL) return;
    let live = true;
    setAnswer({ kind: "asking" });
    void (async () => {
      // The phone's anonymous name first, so the door knows who scanned.
      const token = await guestSession(API_URL, localSessionStore());
      const a = await askTheDoor(API_URL, {
        code,
        signature,
        device: localDevice(),
        session: token,
      });
      if (!live) return;
      setSession(token);
      setAnswer(a);
    })();
    return () => {
      live = false;
    };
  }, [code, signature, attempt]);
  return { answer, session, retry: () => setAttempt((n) => n + 1) };
}

function DoorRefused({
  answer,
  onRetry,
}: {
  answer: Extract<DoorAnswer, { kind: "rejected" | "unreachable" }>;
  onRetry: () => void;
}) {
  const message = refusalMessage(answer);
  const retriable = answer.kind === "unreachable" || RETRIABLE.includes(answer.reason);
  return (
    <>
      <Card>
        <p role="alert" className="text-lg font-semibold" data-testid="door-refused">
          {message}
        </p>
      </Card>
      {retriable && (
        <Button variant="secondary" full onClick={onRetry}>
          {t("door.retry")}
        </Button>
      )}
    </>
  );
}

function NameYourself({ onJoin }: { onJoin: (nickname: string) => void }) {
  const id = useId();
  const [suggestion] = useState(
    () => SUGGESTIONS[Math.floor(Math.random() * SUGGESTIONS.length)] ?? "nick.otter",
  );
  const [name, setName] = useState(() => t(suggestion));
  const [invalid, setInvalid] = useState(false);

  function submit(e: FormEvent) {
    e.preventDefault();
    const clean = name.replace(/\s+/g, " ").trim();
    if (!clean) {
      setInvalid(true);
      return;
    }
    onJoin(clean);
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-1 flex-col gap-4">
      <Card>
        <label htmlFor={id} className="block font-semibold">
          {t("join.nameLabel")}
        </label>
        <p className="mt-1 text-sm text-ink-muted">
          {invalid ? t("join.nameInvalid") : t("join.nameHint")}
        </p>
        <input
          id={id}
          name="nickname"
          autoComplete="nickname"
          maxLength={20}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setInvalid(false);
          }}
          aria-invalid={invalid || undefined}
          className="mt-4 min-h-target w-full rounded-2xl border-2 border-line bg-well px-4 text-lg text-ink aria-invalid:border-rose"
        />
      </Card>
      <div className="flex-1" />
      <Button type="submit" full>
        {t("join.join")}
      </Button>
    </form>
  );
}

function Table({
  code,
  ticket,
  session,
  nickname,
  token,
  onLeave,
}: {
  code: string;
  ticket: string | null;
  session: string | null;
  nickname: string;
  token: string | null;
  onLeave: () => void;
}) {
  const { state, client } = useLobby(LOBBY_URL, code, nickname, token, ticket, session);
  // A code this phone stacked from the scan screen: tell the table once seated.
  const [stackedTicket, setStackedTicket] = useState(() => loadSeat(code)?.stacked ?? null);
  useEffect(() => {
    if (state.phase !== "seated" || !stackedTicket) return;
    client.stacked(stackedTicket);
    setStackedTicket(null);
  }, [state.phase, stackedTicket, client]);
  const status = statusKey(state);
  const becameHost = useBecameHost(state.lobby?.hostId ?? null, state.memberId);
  const [agePassed, setAgePassed] = useState(() => hasPassedAgeGate());
  const [refused, setRefused] = useState(false);
  const gate = state.join.ageGate;
  useEffect(() => {
    setErrorTags({
      lobby_id: code,
      game_id: state.game?.game.gameId ?? null,
      game_version: state.game?.game.gameVersion ?? null,
    });
  }, [code, state.game?.game.gameId, state.game?.game.gameVersion]);

  if (
    state.phase === "expired" ||
    state.phase === "full" ||
    state.phase === "bad-nickname" ||
    state.phase === "removed" ||
    state.phase === "no-ticket"
  ) {
    return (
      <>
        <Card>
          <p role="alert" className="text-lg font-semibold">
            {t(status)}
          </p>
        </Card>
        <div className="flex-1" />
        <Button
          variant="secondary"
          full
          onClick={() => {
            client.leave();
            onLeave();
          }}
        >
          {t(state.phase === "removed" ? "join.rejoin" : "join.startOver")}
        </Button>
      </>
    );
  }

  const members = state.lobby?.members ?? [];
  const hostId = state.lobby?.hostId ?? null;

  if (refused) {
    return (
      <>
        <Card>
          <p role="alert" className="text-lg font-semibold">
            {t("age.underage", { min: gate.minimumAge })}
          </p>
        </Card>
        <div className="flex-1" />
        <Button variant="secondary" full onClick={onLeave}>
          {t("join.startOver")}
        </Button>
      </>
    );
  }

  if (gate.mode !== "off" && !agePassed && state.phase !== "connecting") {
    return (
      <>
        <p role="status" className="text-sm text-mint">
          {t(status)}
        </p>
        <AgeGate
          minimumAge={gate.minimumAge}
          onPass={() => setAgePassed(true)}
          onRefuse={() => {
            client.leave();
            setRefused(true);
          }}
        />
      </>
    );
  }

  if (state.game && state.memberId) {
    return (
      <>
        <p
          role="status"
          className={state.phase === "seated" ? "text-sm text-mint" : "text-sm text-ink-muted"}
        >
          {t(status)}
        </p>
        <OnlineGame
          state={state}
          memberId={state.memberId}
          members={members}
          hostId={hostId}
          client={client}
        />
      </>
    );
  }

  const isHost = state.memberId !== null && state.memberId === hostId;
  const isSteward = state.memberId !== null && state.memberId === state.lobby?.stewardId;
  const breather = state.lobby?.interlude?.kind === "breather";
  return (
    <>
      <p
        role="status"
        className={state.phase === "seated" ? "text-sm text-mint" : "text-sm text-ink-muted"}
      >
        {t(status)}
      </p>

      <StackToast
        stacked={state.stacked}
        guest={subjectOf(session)?.startsWith("guest:") ?? false}
      />

      {becameHost && (
        <Card>
          <p role="status" className="font-semibold" data-testid="host-notice">
            {t("host.youAreHost")}
          </p>
        </Card>
      )}

      {state.lobby?.ended && <Recap lobby={state.lobby} isHost={isHost} client={client} />}

      <Card>
        <h2 className="font-semibold">{t("join.seated", { count: members.length })}</h2>
        <ul className="mt-3 flex flex-col gap-4" data-testid="members">
          {members.map((m) => (
            <Seat
              key={m.id}
              member={m}
              isMe={m.id === state.memberId}
              isHost={m.id === hostId}
              canMakeHost={isHost && !state.lobby?.ended}
              onFlagLeft={() => client.flagLeft(m.id)}
              onMakeHost={() => client.transferHost(m.id)}
              onRemove={() => client.removeMember(m.id)}
              onRename={(nickname) => client.rename(nickname)}
            />
          ))}
        </ul>
      </Card>

      {!state.lobby?.ended && <PresenceControls state={state} client={client} />}

      {(isHost || isSteward) && !state.lobby?.ended && wantsThread(state.catalogue) && (
        <ThatsOne count={state.lobby?.callbacks ?? 0} members={members} client={client} />
      )}

      {API_URL && !state.lobby?.ended && <AddCode lobbyId={code} client={client} />}

      {API_URL && subjectOf(session)?.startsWith("guest:") && (
        <Card tone="sunken">
          <p className="text-sm text-ink-muted">{t("join.keepShareHint")}</p>
          <div className="mt-3">
            <Link to="/account" search={{ c: code }} className="contents">
              <Button variant="secondary" full tabIndex={-1}>
                {t("join.keepShare")}
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {state.lobby?.ended ? null : state.lobby?.vote && state.memberId ? (
        <TableVoteCard lobby={state.lobby} memberId={state.memberId} client={client} />
      ) : breather && state.lobby ? (
        <Breather
          lobby={state.lobby}
          catalogue={state.catalogue}
          canDrive={isHost || isSteward}
          isHost={isHost}
          client={client}
        />
      ) : (
        <>
          <TableSetupCard setup={state.lobby?.setup ?? null} canEdit={isHost} client={client} />
          {isHost && state.lobby && <NightControls lobby={state.lobby} client={client} />}
          {isHost && (
            <Button variant="secondary" full onClick={() => client.startVote()}>
              {t("vote.start")}
            </Button>
          )}
          {isHost && (
            <Button variant="ghost" full onClick={() => client.endNight()}>
              {t("host.end")}
            </Button>
          )}
          {isHost ? (
            <GamePicker state={state} client={client} />
          ) : (
            <Card tone="sunken">
              <p className="text-sm text-ink-muted">
                {state.lobby?.night ? t("night.waiting") : t("join.tableHint")}
              </p>
            </Card>
          )}
        </>
      )}

      <div className="flex-1" />
      <Button
        variant="ghost"
        full
        onClick={() => {
          client.leave();
          onLeave();
        }}
      >
        {t("join.leave")}
      </Button>
    </>
  );
}

const PRESENCE: Record<PublicMember["status"], MessageKey> = {
  active: "join.here",
  "stepped-out": "join.steppedOut",
  away: "join.away",
};

/**
 * One seat. Everyone but you carries a "they left" button: design decision
 * from Nat — if a majority of the people still at the table say somebody has
 * gone home, the seat is freed and they can take a new one if they reappear.
 * The count is shown, never who voted.
 */
function Seat({
  member,
  isMe,
  isHost,
  canMakeHost,
  onFlagLeft,
  onMakeHost,
  onRemove,
  onRename,
}: {
  member: PublicMember;
  isMe: boolean;
  isHost: boolean;
  /** The viewer is the host and may hand the job to this seat. */
  canMakeHost: boolean;
  onFlagLeft: () => void;
  onMakeHost: () => void;
  onRemove: () => void;
  onRename: (nickname: string) => void;
}) {
  const [said, setSaid] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const votes = member.leftVotes;
  return (
    <li className="flex flex-col gap-1" data-testid="seat">
      <div className="flex items-center justify-between gap-3">
        <span className={isMe ? "font-bold" : ""}>
          {member.nickname}
          {isHost ? ` · ${t("join.host")}` : ""}
          {isMe ? ` · ${t("join.you")}` : ""}
        </span>
        <span className={member.status === "active" ? "text-mint" : "text-ink-muted"}>
          {t(PRESENCE[member.status])}
        </span>
      </div>
      {!isMe && (
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            className="px-3 text-sm"
            disabled={said}
            onClick={() => {
              setSaid(true);
              onFlagLeft();
            }}
          >
            {said ? t("join.theyLeftDone") : t("join.theyLeft")}
          </Button>
          {votes > 0 && (
            <span className="text-sm text-ink-muted" data-testid="left-votes">
              {t("join.theyLeftVotes", { votes, needed: member.leftNeeded })}
            </span>
          )}
          {canMakeHost && !isHost && (
            <Button variant="ghost" className="px-3 text-sm" onClick={onMakeHost}>
              {t("host.makeHost")}
            </Button>
          )}
          {canMakeHost && !isHost && !confirming && (
            <Button variant="ghost" className="px-3 text-sm" onClick={() => setConfirming(true)}>
              {t("host.remove")}
            </Button>
          )}
        </div>
      )}
      {confirming && (
        <div className="flex flex-col gap-2" data-testid="remove-confirm">
          <p className="text-sm">{t("host.removeConfirm", { name: member.nickname })}</p>
          <Button
            variant="secondary"
            full
            onClick={() => {
              setConfirming(false);
              onRemove();
            }}
          >
            {t("host.removeYes")}
          </Button>
          <Button variant="ghost" full onClick={() => setConfirming(false)}>
            {t("host.removeNo")}
          </Button>
        </div>
      )}
      {isMe && renaming === null && (
        <Button
          variant="ghost"
          className="self-start px-3 text-sm"
          onClick={() => setRenaming(member.nickname)}
        >
          {t("join.rename")}
        </Button>
      )}
      {isMe && renaming !== null && (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const clean = renaming.replace(/\s+/g, " ").trim();
            if (clean) onRename(clean);
            setRenaming(null);
          }}
        >
          <input
            aria-label={t("join.nameLabel")}
            maxLength={20}
            value={renaming}
            onChange={(e) => setRenaming(e.target.value)}
            className="min-h-target min-w-0 flex-1 rounded-2xl border-2 border-line bg-well px-4 text-lg text-ink"
          />
          <Button type="submit" className="px-4">
            {t("join.renameSave")}
          </Button>
        </form>
      )}
    </li>
  );
}

/**
 * Design doc 6.9: the new host gets a small notice. Only when the job
 * *moves* to this phone: the first host, or a restored seat that was
 * already host, sees nothing. Shown until the job moves again.
 */
function useBecameHost(hostId: string | null, memberId: string | null): boolean {
  const previous = useRef<string | null | undefined>(undefined);
  const [became, setBecame] = useState(false);
  useEffect(() => {
    if (hostId === null || memberId === null) return;
    const before = previous.current;
    previous.current = hostId;
    if (before === undefined) return;
    if (before !== hostId) setBecame(hostId === memberId);
  }, [hostId, memberId]);
  return became;
}

/** "Be right back" and "I'm back", for whoever is holding this phone. */
function PresenceControls({ state, client }: { state: LobbyClientState; client: LobbyClient }) {
  const me = state.lobby?.members.find((m) => m.id === state.memberId);
  if (!me) return null;
  return me.status === "away" ? (
    <Card tone="sunken">
      <p className="text-sm text-ink-muted">{t("join.awayHint")}</p>
      <div className="mt-3">
        <Button full onClick={() => client.imBack()}>
          {t("join.imBack")}
        </Button>
      </div>
    </Card>
  ) : (
    <Card tone="sunken">
      <p className="text-sm text-ink-muted">{t("join.presenceHint")}</p>
      <div className="mt-3">
        <Button variant="secondary" full onClick={() => client.stepAway()}>
          {t("join.beRightBack")}
        </Button>
      </div>
    </Card>
  );
}

function statusKey(state: LobbyClientState): MessageKey {
  switch (state.phase) {
    case "connecting":
      return "join.status.connecting";
    case "seated":
      return state.restored ? "join.status.restored" : "join.status.seated";
    case "reconnecting":
      return "join.status.reconnecting";
    case "expired":
      return "join.status.expired";
    case "full":
      return "join.status.full";
    case "bad-nickname":
      return "join.status.badNickname";
    case "removed":
      return "join.status.removed";
    case "no-ticket":
      return "join.status.noTicket";
    case "closed":
      return "join.status.closed";
  }
}
